import { useState } from "react";

import { AlertTriangle, Pencil, RotateCw } from "lucide-react";
import { useDispatch } from "react-redux";

import { useRetrySummaryGeneration } from "@/hooks/mutations/use-files";
import { setMessage } from "@/redux/notification";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { ConfirmationModal } from "@components/ConfirmationModal";
import { Button } from "@components/ui/button";

type SummaryStatusCellProps = {
  file: DocSorterFileSchema;
  onEdit: (file: DocSorterFileSchema) => void;
};

export const SummaryStatusCell = ({ file, onEdit }: SummaryStatusCellProps) => {
  const dispatch = useDispatch();
  const retrySummaryGeneration = useRetrySummaryGeneration();
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  const status = file.status;
  const isGenerating = status === "PROCESSING" || status === "TO_BE_PROCESSED";
  const isFailed = status === "FAILED";
  const description = file.short_summary?.trim() ?? "";

  const triggerRetry = () => {
    retrySummaryGeneration.mutate(file.id, {
      onSuccess: () => {
        dispatch(
          setMessage({
            type: "success",
            content: "Description regeneration started",
          })
        );
        setShowRegenerateConfirm(false);
      },
      onError: (error) => {
        dispatch(
          setMessage({
            type: "error",
            content: error.message || "Failed to regenerate description",
          })
        );
      },
    });
  };

  const handleRetryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (description) {
      // Confirm only when there is text to lose
      setShowRegenerateConfirm(true);
    } else {
      triggerRetry();
    }
  };

  if (isGenerating) {
    return (
      <div className="flex items-center gap-2 text-sm text-foreground-muted">
        <LoadingIcon className="size-3.5 animate-spin-ccw" />
        <span className="italic">Generating description…</span>
      </div>
    );
  }

  if (isFailed) {
    return (
      <>
        <div className="flex items-center justify-between gap-2">
          <div
            className="flex min-w-0 items-center gap-2 text-sm text-destructive"
            title="The AI description couldn't be generated for this file. If it's a scanned or non-PDF document, try re-uploading it as a text-based PDF, or write the description manually."
          >
            <AlertTriangle className="size-3.5 flex-shrink-0" />
            <span className="truncate">Failed to generate</span>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRetryClick}
              disabled={retrySummaryGeneration.isPending}
              className="size-6 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
              title="Retry generation"
            >
              <RotateCw className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(file);
              }}
              className="size-6 p-0 text-foreground-muted hover:text-foreground"
              title="Edit description manually"
            >
              <Pencil className="h-3 w-3" />
            </Button>
          </div>
        </div>

        <ConfirmationModal
          type="warning"
          icon={<RotateCw className="size-14 text-destructive" />}
          title="Regenerate Document Description?"
          description="This will replace the current description with a new AI-generated one. The existing text will be lost. Continue?"
          confirmButtonText="Regenerate"
          cancelButtonText="Keep Current"
          onConfirm={triggerRetry}
          isLoading={retrySummaryGeneration.isPending}
          open={showRegenerateConfirm}
          onOpenChange={setShowRegenerateConfirm}
        />
      </>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0 overflow-hidden break-words text-sm text-foreground">
        {description || (
          <span className="italic text-foreground-muted">No description</span>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(file);
        }}
        className="h-6 w-6 flex-shrink-0 p-0 text-foreground-muted hover:bg-primary-50 hover:text-primary"
        title="Edit description"
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};
